import { useState, useRef } from 'react';
import serviceMethods from '../../service';
import LoadingSpinner from '../LoadingSpinner';
import SchedulerLocationDropdown from '../SchedulerLocationDropdown';
import { useScheduler } from '../../contexts/scheduler';

const AdminTimesheets = () => {
  const { locationDropdownOptions } = useScheduler();

  const hiddenFileInput = useRef(null);
  const [loadingStarted, setLoadingStarted] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [resultsSuccess, setResultsSuccess] = useState([]);
  const [resultsNoEmail, setResultsNoEmail] = useState([]);
  const [resultsSkipped, setResultsSkipped] = useState([]);
  const [resultsError, setResultsError] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(8); //a number.   This sets the default value to Prestige Gunite Austin

  const handleUpload = async e => {
    const fileUploaded = e.target.files[0];
    if (!fileUploaded) return;

    setLoadingStarted(true);
    setUploadMessage(null);
    setResultsSuccess([]);
    setResultsNoEmail([]);
    setResultsSkipped([]);
    setResultsError([]);

    const formData = new FormData();
    formData.append('file', fileUploaded);
    formData.append('locationId', selectedLocationId);
    try {
      const res = await serviceMethods.uploadTimesheet(formData);
      const data = await res.json();

      // 2. Handle Server-Side Errors (like the .xls check)
      if (!res.ok) {
        setUploadMessage(data.message || 'An unknown error occurred.');
        setLoadingStarted(false);
        return;
      }

      // 3. Handle Success Case
      if (data.message) {
        setUploadMessage(data.message);
      }

      if (data.results) {
        const { results } = data;
        setResultsSuccess(results.success || []);
        setResultsNoEmail(results.noEmail || []);
        setResultsSkipped(results.skipped || []);
        setResultsError(results.error || []);

        // 4. Handle Debug PDFs (only happens in local dev)
        if (process.env.NODE_ENV === 'development') {
          if (results.pdfs && results.pdfs.length > 0) {
            results.pdfs.forEach(pdf => {
              const byteCharacters = atob(pdf.base64);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const fileBlob = new Blob([byteArray], { type: 'application/pdf' });
              const fileURL = URL.createObjectURL(fileBlob);
              window.open(fileURL, '_blank');
            });
          }
        }
      }
    } catch (err) {
      setUploadMessage('Network error: Could not reach the server.');
      console.error(err);
    } finally {
      setLoadingStarted(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      <h1 className='text-xl font-semibold text-dark-blue mb-6'>Timesheets</h1>
      <section className='flex flex-col w-full gap-6 mb-6 items-start justify-between bg-opacity-10 bg-navy-bright rounded-lg border border-navy-light px-2 min-[400px]:px-5 pt-4 pb-8'>
        <div className='flex flex-col w-full justify-between gap-6'>
          <h2 className='text-lg font-semibold text-dark-blue'>
            Upload Branch Timesheet for processing
          </h2>

          <SchedulerLocationDropdown
            value={selectedLocationId}
            placeholder={'Timesheet Location:'}
            options={locationDropdownOptions}
            onChange={e => {
              setSelectedLocationId(parseInt(e.target.value));
            }}
            role={'scheduler'}
          />
        </div>

        <div className='flex w-full justify-center gap-4 pb-4'>
          <form encType='multipart/form-data' className='w-auto'>
            <input type='file' className='hidden' onChange={handleUpload} ref={hiddenFileInput} />
            <button
              onClick={() => {
                setUploadMessage(null);
                hiddenFileInput.current.click();
              }}
              type='button'
              className='flex justify-center text-sm font-semibold py-2 px-3 w-40 h-[40px] rounded bg-white text-dark-blue border border-dark-blue hover:text-white hover:bg-dark-blue active:bg-white active:text-dark-blue'
            >
              {loadingStarted ? <LoadingSpinner /> : 'Upload'}
            </button>
          </form>
        </div>

        {uploadMessage && (
          <div
            className={`w-full p-4 mb-4 rounded ${uploadMessage.includes('Unsupported') || uploadMessage.includes('Error') ? 'bg-red-100 text-red-700 border border-red-400' : 'bg-green-100 text-green-700 border border-green-400'}`}
          >
            {uploadMessage}
          </div>
        )}
        {resultsSuccess?.length !== 0 && <div className='w-full h-px bg-navy-light my-4' />}

        {resultsSuccess?.length !== 0 ? (
          <div>
            <h3 className='font-bold text-dark-blue'>
              Timesheet processed and email sent successfully
            </h3>
            {resultsSuccess?.map((item, index) => (
              <p key={`${item}-${index}`} className='pl-4'>
                - {item}
              </p>
            ))}
          </div>
        ) : null}

        {resultsSuccess?.length !== 0 && resultsNoEmail?.length !== 0 && (
          <div className='w-full h-px bg-navy-light my-4' />
        )}

        {resultsNoEmail?.length !== 0 ? (
          <div>
            <h3 className='font-bold text-dark-blue'>Timesheet processed, no email listed</h3>
            {resultsNoEmail?.map((item, index) => (
              <p key={`${item}-${index}`} className='pl-4'>
                - {item}
              </p>
            ))}
          </div>
        ) : null}

        {(resultsSkipped?.length !== 0) !== 0 && resultsNoEmail?.length !== 0 && (
          <div className='w-full h-px bg-navy-light my-4' />
        )}

        {resultsSkipped?.length !== 0 ? (
          <div>
            <h3 className='font-bold text-dark-blue'>Sheets skipped</h3>
            {resultsSkipped?.map((item, index) => (
              <p key={`${item}-${index}`} className='pl-4'>
                - {item}
              </p>
            ))}
          </div>
        ) : null}

        {resultsError?.length !== 0 && <div className='w-full h-px bg-navy-light my-4' />}

        {resultsError?.length !== 0 ? (
          <div>
            <h3 className='font-bold text-red-600'>Error in processing</h3>
            {resultsError?.map((item, index) => (
              <p key={`${item}-${index}`} className='pl-4'>
                - {item}
              </p>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default AdminTimesheets;
